import { useCallback } from 'react';
import { DICOMAnonymizerService, ModelType } from '~/anonymizer/anonymizer';
import { handleError } from '~/utils/errors';

const _anonymizer = new DICOMAnonymizerService();

type DicomObject = {
    [key: string]: string | number | DicomObject[] | DicomObject;
    [Symbol.iterator](): Iterator<DicomObject>;
};

const useAnonymizer = () => {
    const commonAnonymizedAttributes = [
        'PatientID',
        'PatientName',
        'SOPInstanceUID',
        'SeriesInstanceUID',
        'StudyInstanceUID',
        'AccessionNumber',
    ];

    const validateAnonymization = (
        originalDatasets: DicomObject[],
        anonymizedDatasets: DicomObject[]
    ): boolean => {
        for (let i = 0; i < originalDatasets.length; i++) {
            const [original] = originalDatasets[i];
            const [anonymized] = anonymizedDatasets[i];

            for (const attr of commonAnonymizedAttributes) {
                if (original[attr] && original[attr] === anonymized[attr]) {
                    console.error(
                        `Anonymization did not change ${attr} in file ${i}. Preventing upload.`
                    );
                    return false;
                }
            }
        }
        return true;
    };

    const anonymizeBlobs = useCallback(
        async (blobs: Blob[], modelType: ModelType): Promise<Blob[] | null> => {
            try {
                const buffers = await Promise.all(
                    blobs.map((blob) => blob.arrayBuffer())
                );
                const anonymizedBuffers = _anonymizer.anonymizeFiles(
                    buffers,
                    modelType
                );
                if (!anonymizedBuffers) {
                    throw new Error('Anonymization failed: No data available');
                }

                const originalDatasets = _anonymizer.parseBuffers(buffers);
                const anonymizedDatasets =
                    _anonymizer.parseBuffers(anonymizedBuffers);
                if (
                    !validateAnonymization(
                        originalDatasets as unknown as DicomObject[],
                        anonymizedDatasets as unknown as DicomObject[]
                    )
                ) {
                    throw new Error('Anonymization verification failed.');
                }

                return anonymizedBuffers.map(
                    (buffer: ArrayBuffer) =>
                        new Blob([buffer], { type: 'application/dicom' })
                );
            } catch (error) {
                handleError('Anonymization failed:', `${error}`);
                return null;
            }
        },
        []
    );

    const reidentifyFiles = useCallback(
        async (files: File[], modelType: ModelType): Promise<File[] | null> => {
            try {
                const buffers = await Promise.all(
                    files.map((file) => file.arrayBuffer())
                );
                const reidentifiedBuffers = _anonymizer.reidentifyFiles(
                    buffers,
                    modelType
                );
                if (!reidentifiedBuffers)
                    throw new Error('Re-identification failed.');

                return reidentifiedBuffers.map(
                    (buffer, index) =>
                        new File([buffer], files[index].name, {
                            type: 'application/dicom',
                        })
                );
            } catch (error) {
                handleError('Re-identification failed:', `${error}`);
                return null;
            }
        },
        []
    );

    return { anonymizeBlobs, reidentifyFiles, anonymizer: _anonymizer };
};

export default useAnonymizer;
